import React from 'react';
import { LatLngLiteral } from 'leaflet';
import { useEventContext } from './EventProvider';
import { calculateSplits } from 'utils/splitHelpers';
import { TrackPoint } from './EventProvider';
import { useSearchParams } from 'react-router-dom';

export type ControlType = {
  id: number;
  name: string;
  type: 'center' | 'default' | 'circle' | 'square' | 'triangle';
  position: TrackPoint;
  siCode: number;
};

export interface SplitType {
  firstControl: ControlType;
  secondControl: ControlType;
}

export type SplitContextType = {
  splits: SplitType[];
  setSplits: React.Dispatch<React.SetStateAction<SplitType[]>>;
  controls: ControlType[];
  setControls: React.Dispatch<React.SetStateAction<ControlType[]>>;
  options: SplitOptionsType;
  setOptions: React.Dispatch<React.SetStateAction<SplitOptionsType>>;
  activeSplits: number[];
  setActiveSplits: React.Dispatch<React.SetStateAction<number[]>>;
  time: number;
  setTime: React.Dispatch<React.SetStateAction<number>>;
  maxTime: number;
  setMaxTime: React.Dispatch<React.SetStateAction<number>>;
  mapCenter: LatLngLiteral | null;
  setMapCenter: React.Dispatch<React.SetStateAction<LatLngLiteral | null>>;
  mapBearing: number;
  setMapBearing: React.Dispatch<React.SetStateAction<number>>;
  mapTransitionMs: number;
  setMapTransitionMs: React.Dispatch<React.SetStateAction<number>>;
  mapFitPoints: [LatLngLiteral, LatLngLiteral] | null;
  setMapFitPoints: React.Dispatch<React.SetStateAction<[LatLngLiteral, LatLngLiteral] | null>>;
  followedPlayerId: number | null;
  setFollowedPlayerId: React.Dispatch<React.SetStateAction<number | null>>;
};

export const SplitContext = React.createContext<SplitContextType>({
  splits: [],
  setSplits: () => { },
  controls: [],
  setControls: () => { },
  activeSplits: [],
  setActiveSplits: () => { },
  options: {
    weight: 3,
    opacity: 100,
    timeGradient: false,
    autoZoom: false,
    speed: 10
  },
  setOptions: () => { },
  time: 0,
  setTime: () => { },
  maxTime: 0,
  setMaxTime: () => { },
  mapCenter: null,
  setMapCenter: () => { },
  mapBearing: 0,
  setMapBearing: () => { },
  mapTransitionMs: 0,
  setMapTransitionMs: () => { },
  mapFitPoints: null,
  setMapFitPoints: () => { },
  followedPlayerId: null,
  setFollowedPlayerId: () => { }
});

export type SplitOptionsType = {
  weight: number;
  opacity: number;
  timeGradient: boolean;
  autoZoom: boolean;
  speed: number;
};

export const SplitContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { event } = useEventContext();
  const initialSplits = calculateSplits(event.controlsAnalytic);
  const [splits, setSplits] = React.useState<SplitType[]>(initialSplits);
  const [controls, setControls] = React.useState<Array<ControlType>>(event.controlsAnalytic);
  const [options, setOptions] = React.useState<SplitOptionsType>({
    weight: 3,
    opacity: 100,
    timeGradient: false,
    autoZoom: false,
    speed: 10
  });
  const [activeSplits, setActiveSplits] = React.useState<number[]>(
    initialSplits.map((_, index) => index)
  );
  const [time, setTime] = React.useState<number>(0);
  const [maxTime, setMaxTime] = React.useState<number>(0);
  const [mapCenter, setMapCenter] = React.useState<LatLngLiteral | null>(null);
  const [mapBearing, setMapBearing] = React.useState(0);
  const [mapTransitionMs, setMapTransitionMs] = React.useState(0);
  const [mapFitPoints, setMapFitPoints] = React.useState<[LatLngLiteral, LatLngLiteral] | null>(null);
  const [searchParams] = useSearchParams();
  const competitorFromUrl = searchParams.get('competitor');
  const [followedPlayerId, setFollowedPlayerId] = React.useState<number | null>(() => {
    if (!competitorFromUrl) return null;
    const id = Number(competitorFromUrl);
    return Number.isNaN(id) ? null : id;
  });

  return (
    <SplitContext.Provider
      value={{
        splits,
        setSplits,
        controls,
        setControls,
        options,
        setOptions,
        activeSplits,
        setActiveSplits,
        time,
        setTime,
        maxTime,
        setMaxTime,
        mapCenter,
        setMapCenter,
        mapBearing,
        setMapBearing,
        mapTransitionMs,
        setMapTransitionMs,
        mapFitPoints,
        setMapFitPoints,
        followedPlayerId,
        setFollowedPlayerId
      }}
    >
      {children}
    </SplitContext.Provider>
  );
};

export const useSplitContext = () => {
  return React.useContext(SplitContext);
};
